import { useState, useEffect, useRef, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { fileSize } from 'components/misc/functions';
import { Camera } from 'components';
import imageCompression from 'browser-image-compression';
import { HugeiconsIcon } from '@hugeicons/react';
import { Camera01Icon, Image01Icon, Delete02Icon, Cancel01Icon } from '@hugeicons/core-free-icons';

function Photo({ label, name, value, setValue }) {
    const [isOpen, setIsOpen] = useState(false);
    const [preview, setPreview] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        const mobile = /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        setIsMobile(mobile);
    }, [name]);

    const handleImageSet = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const options = { maxSizeMB: 1, useWebWorker: true };
        const compressed = await imageCompression(file, options);
        const renamed = new File([compressed], `${name}.jpg`, { type: compressed.type });
        setValue(renamed);
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => setPreview(reader.result);
    };

    const handleTrigger = () => {
        if (name === 'selfie-photo' && isMobile) {
            fileInputRef.current.click();
        } else {
            setIsOpen(true);
        }
    };

    const clearPhoto = () => {
        setPreview(null);
        setValue('');
    };

    const isSelfie = name === 'selfie-photo';

    return (
        <>
        <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                {label}
            </label>

            {preview ? (
                /* Filled state */
                <div className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-white/[.08] bg-white dark:bg-gray-900 p-3">
                    <img
                        src={preview}
                        alt={label}
                        className="w-16 h-16 rounded-lg object-cover shrink-0 border border-gray-100 dark:border-white/[.08]"
                    />
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{value?.name}</p>
                        {value?.size && (
                            <p className="text-xs text-gray-400 mt-0.5">{fileSize(value.size)}</p>
                        )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <button
                            type="button"
                            onClick={handleTrigger}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                        >
                            <HugeiconsIcon icon={Camera01Icon} size={13} />
                            Retake
                        </button>
                        <button
                            type="button"
                            onClick={clearPhoto}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                            aria-label="Remove photo"
                        >
                            <HugeiconsIcon icon={Delete02Icon} size={15} />
                        </button>
                    </div>
                </div>
            ) : (
                /* Empty state */
                <button
                    type="button"
                    onClick={handleTrigger}
                    className="w-full flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-gray-200 dark:border-white/[.10] bg-gray-50/50 dark:bg-white/[.02] hover:border-amber-300 dark:hover:border-amber-500/40 hover:bg-amber-50/30 dark:hover:bg-amber-500/5 transition-all py-8 px-4"
                >
                    <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                        <HugeiconsIcon icon={Camera01Icon} size={20} className="text-gray-400 dark:text-gray-500" />
                    </div>
                    <div className="text-center">
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Take a photo</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Click to open camera</p>
                    </div>
                </button>
            )}

            {isSelfie && (
                <div className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 px-3 py-2.5">
                    <HugeiconsIcon icon={Image01Icon} size={14} className="text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
                        Ensure your face fills 90% of the frame and you're in a well-lit environment.
                    </p>
                </div>
            )}
        </div>

        <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="user"
            onChange={handleImageSet}
            className="hidden"
        />

        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={() => setIsOpen(false)}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
                    leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
                            leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-lg rounded-2xl bg-white dark:bg-gray-900 shadow-2xl overflow-hidden">
                                {/* Modal header */}
                                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-white/[.08]">
                                    <div>
                                        <Dialog.Title className="text-sm font-semibold text-gray-900 dark:text-white">
                                            {label}
                                        </Dialog.Title>
                                        {isSelfie && (
                                            <p className="text-xs text-gray-400 mt-0.5">Position your face in the oval guide</p>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setIsOpen(false)}
                                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                    >
                                        <HugeiconsIcon icon={Cancel01Icon} size={16} />
                                    </button>
                                </div>

                                {/* Camera view */}
                                <div className="bg-black">
                                    <Camera
                                        name={name}
                                        setPreview={setPreview}
                                        setValue={setValue}
                                        closeModal={() => setIsOpen(false)}
                                    />
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
        </>
    );
}

export default Photo;
